/**
 * Admin → Dealer desk → Risk desk and Daily summary (phase 5). The risk desk refreshes every 10 s:
 * exposure per instrument with the hedge target, biggest winners and losers, margin watch, negative
 * balances, long-held positions and the correction watch. The daily summary is what staff did with
 * money and trades on one UTC day; the worker sends each finished day to the CRM.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { addDays, D, fmtDate, fmtDateTime, fmtUsd, rangeLabel, todayIn, type DayRange } from '@platform/shared';
import { ClientCell } from '../components/ClientCell';
import { isCustom, lastDays, TimeFrameSelect, type Period } from '../components/DateRange';
import { InstrumentLabel } from '../components/InstrumentIcon';
import { cx, Empty, Spinner, TradeId } from '../components/ui';
import { useLoad } from '../stores/account';

const th = 'px-3 py-2 text-left text-[11px] font-medium text-muted whitespace-nowrap';
const td = 'px-3 py-2 whitespace-nowrap';
const num = 'num text-right';
const tone = (x: string | null) => (x === null ? '' : D.from(x).isNeg() ? 'text-down' : D.from(x).isPos() ? 'text-up' : '');
const pct = (x: string | null) => (x === null ? '—' : `${D.from(x).mul(100).toFixed(1)}%`);
const openAccount = (id: string) => `/admin?tab=accounts&account=${id}`;

function Card({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-line bg-panel">
      <h3 className="m-0 border-b border-line px-3 py-2.5 text-sm font-semibold">
        {title} {note && <span className="text-[11px] font-normal text-muted">· {note}</span>}
      </h3>
      <div className="overflow-x-auto">{children}</div>
    </section>
  );
}
function T({ head, right = [], children }: { head: string[]; right?: string[]; children: ReactNode }) {
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h} className={cx(th, right.includes(h) && 'text-right')}>
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}
const Name = ({ id, name, crmId }: { id: string; name: string | null; crmId?: string | null }) => <ClientCell name={name} crmId={crmId} href={openAccount(id)} />;

interface AccRow {
  accountId: string;
  name: string | null;
  externalClientId: string | null;
  online: boolean;
  lastSeenAt: string | null;
  balance: string;
  credit: string;
  equity: string;
  openPnl: string;
  marginLevel: string | null;
  openPositions: number;
  negativeSince?: string | null;
  status?: string;
}
interface Risk {
  tiles: { clientOpenPnl: string; grossExposure: string; unhedged: string; marginWatch: number; negativeBalances: number; correctionsWaiting: number };
  levels: { warning: string; critical: string; stopOut: string };
  exposure: { symbol: string; clients: number; positions: number; netUnits: string; netNotional: string | null; grossNotional: string | null; clientOpenPnl: string; hedgeTargetNotional: string | null; unhedgedNotional: string | null }[];
  topWinners: AccRow[];
  topLosers: AccRow[];
  marginWatch: AccRow[];
  negativeBalances: AccRow[];
  longDated: { days: number; positions: { positionId: string; tradeId: number | null; accountId: string; name: string | null; externalClientId: string | null; symbol: string; side: string; qty: string; openedAt: string; days: number; swap: string; openPnl: string | null }[] };
  correctionWatch: { staffId: string; name: string; role: string; corrections: number; forClient: number; againstClient: number; net: string; flagged: boolean }[];
  asOf: string;
}

const NEG_STATUS: Record<string, [string, string]> = {
  trading: ['Trading while negative', 'bg-down/15 text-down'],
  credit_only: ['Credit only', 'bg-warn/15 text-warn'],
  awaiting_protection: ['Write-off due', 'bg-warn/15 text-warn'],
};

export function RiskDeskTab() {
  const { data, error, reload } = useLoad<Risk>('/api/admin/desk/risk');
  useEffect(() => {
    const t = window.setInterval(() => void reload(), 10_000);
    return () => window.clearInterval(t);
  }, [reload]);
  if (error) return <p className="m-0 text-xs text-down">{error}</p>;
  if (!data) return <Spinner />;
  const tiles: [string, string, string?][] = [
    ['Client open P&L (house risk)', fmtUsd(data.tiles.clientOpenPnl), tone(data.tiles.clientOpenPnl)],
    ['Gross exposure', fmtUsd(data.tiles.grossExposure)],
    ['Unhedged (after targets)', fmtUsd(data.tiles.unhedged)],
    ['Under the margin alert', String(data.tiles.marginWatch), data.tiles.marginWatch ? 'text-down' : ''],
    ['Negative balances', String(data.tiles.negativeBalances), data.tiles.negativeBalances ? 'text-down' : ''],
    ['Corrections waiting', String(data.tiles.correctionsWaiting)],
  ];
  const accTable = (rows: AccRow[], empty: string) =>
    rows.length ? (
      <T head={['Client', 'Equity', 'Open P&L', 'Margin level']} right={['Equity', 'Open P&L', 'Margin level']}>
        {rows.map((r) => (
          <tr key={r.accountId} className="border-t border-line">
            <td className={td}>
              <Name id={r.accountId} name={r.name} crmId={r.externalClientId} />
            </td>
            <td className={cx(td, num)}>{fmtUsd(r.equity)}</td>
            <td className={cx(td, num, tone(r.openPnl))}>{fmtUsd(r.openPnl)}</td>
            <td className={cx(td, num)}>{pct(r.marginLevel)}</td>
          </tr>
        ))}
      </T>
    ) : (
      <Empty>{empty}</Empty>
    );
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="m-0 text-lg font-semibold">Risk desk</h2>
        <span className="text-xs text-muted">Live · {fmtDateTime(data.asOf)}</span>
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line md:grid-cols-3 xl:grid-cols-6">
        {tiles.map(([l, v, c]) => (
          <div key={l} className="flex flex-col gap-0.5 bg-panel px-3 py-2.5">
            <span className="text-[11px] text-muted">{l}</span>
            <span className={cx('num text-base font-semibold', c)}>{v}</span>
          </div>
        ))}
      </div>
      <Card title="Net exposure by instrument" note="the house holds the other side; hedge targets from Admin → Hedging">
        <T head={['Instrument', 'Clients', 'Net units', 'Net USD', 'Gross USD', 'Client P&L', 'Hedge target', 'Unhedged']} right={['Clients', 'Net units', 'Net USD', 'Gross USD', 'Client P&L', 'Hedge target', 'Unhedged']}>
          {data.exposure.map((e) => (
            <tr key={e.symbol} className="border-t border-line">
              <td className={cx(td, 'font-semibold')}>
                <InstrumentLabel symbol={e.symbol} />
              </td>
              <td className={cx(td, num)}>{e.clients}</td>
              <td className={cx(td, num)}>{D.from(e.netUnits).toFixed(4)}</td>
              <td className={cx(td, num)}>{e.netNotional ? fmtUsd(e.netNotional) : '—'}</td>
              <td className={cx(td, num)}>{e.grossNotional ? fmtUsd(e.grossNotional) : '—'}</td>
              <td className={cx(td, num, tone(e.clientOpenPnl))}>{fmtUsd(e.clientOpenPnl)}</td>
              <td className={cx(td, num)}>{e.hedgeTargetNotional ? fmtUsd(e.hedgeTargetNotional) : '—'}</td>
              <td className={cx(td, num)}>{e.unhedgedNotional ? fmtUsd(e.unhedgedNotional) : '—'}</td>
            </tr>
          ))}
        </T>
        {!data.exposure.length && <Empty>No open positions.</Empty>}
      </Card>
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Biggest client winners" note="their profit is house risk">
          {accTable(data.topWinners, 'No client is in profit.')}
        </Card>
        <Card title="Biggest client losers">{accTable(data.topLosers, 'No client is at a loss.')}</Card>
        <Card title="Margin watch" note={`below ${pct(data.levels.warning)}; stop-out ${pct(data.levels.stopOut)} is automatic`}>
          {data.marginWatch.length ? (
            <T head={['Client', 'Equity', 'Margin level', 'Online / last seen']} right={['Equity', 'Margin level']}>
              {data.marginWatch.map((r) => (
                <tr key={r.accountId} className="border-t border-line">
                  <td className={td}>
                    <Name id={r.accountId} name={r.name} crmId={r.externalClientId} />
                  </td>
                  <td className={cx(td, num)}>{fmtUsd(r.equity)}</td>
                  <td className={cx(td, num, D.from(r.marginLevel!).lt(data.levels.critical) ? 'text-down' : 'text-warn')}>{pct(r.marginLevel)}</td>
                  <td className={cx(td, 'text-muted')}>{r.online ? 'Online' : r.lastSeenAt ? fmtDateTime(r.lastSeenAt) : '—'}</td>
                </tr>
              ))}
            </T>
          ) : (
            <Empty>No account under the alert level.</Empty>
          )}
        </Card>
        <Card title="Negative balances">
          {data.negativeBalances.length ? (
            <T head={['Client', 'Balance', 'Credit', 'Open', 'Since', 'Status']} right={['Balance', 'Credit', 'Open']}>
              {data.negativeBalances.map((r) => {
                const [label, cls] = NEG_STATUS[r.status ?? ''] ?? [r.status ?? '', ''];
                return (
                  <tr key={r.accountId} className="border-t border-line">
                    <td className={td}>
                      <Name id={r.accountId} name={r.name} crmId={r.externalClientId} />
                    </td>
                    <td className={cx(td, num, 'text-down')}>{fmtUsd(r.balance)}</td>
                    <td className={cx(td, num)}>{fmtUsd(r.credit)}</td>
                    <td className={cx(td, num)}>{r.openPositions}</td>
                    <td className={cx(td, 'text-muted')}>{r.negativeSince ? fmtDate(r.negativeSince) : '—'}</td>
                    <td className={td} title={r.status === 'awaiting_protection' ? 'Nothing open: negative balance protection or a zero balance is due' : undefined}>
                      <span className={cx('rounded-full px-2 py-0.5 text-[11px] font-semibold', cls)}>{label}</span>
                    </td>
                  </tr>
                );
              })}
            </T>
          ) : (
            <Empty>No negative balances.</Empty>
          )}
        </Card>
        <Card title="Long-held positions" note={`open more than ${data.longDated.days} days`}>
          {data.longDated.positions.length ? (
            <T head={['Order ID', 'Client', 'Instrument', 'Opened', 'Days', 'Swap so far', 'Open P&L']} right={['Days', 'Swap so far', 'Open P&L']}>
              {data.longDated.positions.map((p) => (
                <tr key={p.positionId} className="border-t border-line">
                  <td className={td}>
                    <TradeId id={p.tradeId} />
                  </td>
                  <td className={td}>
                    <Name id={p.accountId} name={p.name} crmId={p.externalClientId} />
                  </td>
                  <td className={td}>
                    <InstrumentLabel symbol={p.symbol}>
                      {p.symbol} <span className="text-muted">{p.side === 'long' ? 'Buy' : 'Sell'}</span>
                    </InstrumentLabel>
                  </td>
                  <td className={cx(td, 'text-muted')}>{fmtDateTime(p.openedAt)}</td>
                  <td className={cx(td, num)}>{p.days}</td>
                  <td className={cx(td, num, tone(p.swap))}>{fmtUsd(p.swap)}</td>
                  <td className={cx(td, num, tone(p.openPnl))}>{p.openPnl ? fmtUsd(p.openPnl) : '—'}</td>
                </tr>
              ))}
            </T>
          ) : (
            <Empty>No position open that long.</Empty>
          )}
        </Card>
        <Card title="Correction watch" note="last 30 days, by who asked">
          {data.correctionWatch.length ? (
            <>
              <T head={['Staff', 'Corrections', 'For clients', 'Against clients', 'Net']} right={['Corrections', 'For clients', 'Against clients', 'Net']}>
                {data.correctionWatch.map((c) => (
                  <tr key={c.staffId} className={cx('border-t border-line', c.flagged && 'bg-warn/5')}>
                    <td className={td}>
                      {c.name} <span className="text-muted">· {c.role.replace('_', ' ')}</span>
                    </td>
                    <td className={cx(td, num)}>{c.corrections}</td>
                    <td className={cx(td, num)}>{c.forClient}</td>
                    <td className={cx(td, num, c.flagged && 'font-semibold text-warn')}>{c.againstClient}</td>
                    <td className={cx(td, num, tone(c.net))}>{fmtUsd(c.net)}</td>
                  </tr>
                ))}
              </T>
              {data.correctionWatch
                .filter((c) => c.flagged)
                .map((c) => (
                  <p key={c.staffId} className="m-0 px-3 py-2 text-xs text-warn">
                    {c.name}: every correction in 30 days went against the client. Compliance should review.
                  </p>
                ))}
            </>
          ) : (
            <Empty>No corrections in the last 30 days.</Empty>
          )}
        </Card>
      </div>
    </div>
  );
}

interface Summary {
  day: string;
  byAction: { action: string; count: number; total: string | null }[];
  byStaff: { staffId: string; name: string; role: string; actions: number; moneyActions: number; moneyMoved: string }[];
  approvals: { requested: number; approved: number; rejected: number; failed: number; stillPending: number };
}

/** UTC days: one day (the default) or up to 92 for a range. */
const SUMMARY_PERIODS: { id: 'today' | 'yesterday' | '7' | '30'; label: string }[] = [
  { id: 'today', label: 'Today (UTC)' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7', label: 'Last 7 days' },
  { id: '30', label: 'Last 30 days' },
];

function summaryDays(p: Period<(typeof SUMMARY_PERIODS)[number]['id']>): DayRange {
  if (isCustom(p)) return p.range;
  const today = todayIn('utc');
  if (p.id === 'today') return { from: today, to: today };
  if (p.id === 'yesterday') return { from: addDays(today, -1), to: addDays(today, -1) };
  return lastDays(Number(p.id), 'utc');
}

export function DailySummaryTab() {
  const [period, setPeriod] = useState<Period<(typeof SUMMARY_PERIODS)[number]['id']>>({ id: 'today' });
  const days = summaryDays(period);
  const one = days.from === days.to;
  const { data, error } = useLoad<Summary>(`/api/admin/desk/summary?${one ? `date=${days.from}` : `from=${days.from}&to=${days.to}`}`);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-lg font-semibold">{one ? 'Daily summary' : `Summary · ${rangeLabel(days)}`}</h2>
        <TimeFrameSelect label="Summary days (UTC)" zone="utc" maxDays={92} presets={SUMMARY_PERIODS} value={period} onChange={setPeriod} />
      </div>
      <p className="m-0 text-xs text-muted">
        What staff did with money and trades {one ? 'that day' : 'over those days (UTC)'}. Each finished day is also sent to the CRM for compliance.
      </p>
      {error && <p className="m-0 text-xs text-down">{error}</p>}
      {data && (
        <>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-5">
            {(
              [
                ['Requests made', data.approvals.requested],
                ['Approved', data.approvals.approved],
                ['Rejected', data.approvals.rejected],
                ['Failed', data.approvals.failed],
                ['Still waiting (now)', data.approvals.stillPending],
              ] as const
            ).map(([l, v]) => (
              <div key={l} className="flex flex-col gap-0.5 bg-panel px-3 py-2.5">
                <span className="text-[11px] text-muted">{l}</span>
                <span className="num text-base font-semibold">{v}</span>
              </div>
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Card title="By action">
              {data.byAction.length ? (
                <T head={['Action', 'Count', 'Total']} right={['Count', 'Total']}>
                  {data.byAction.map((a) => (
                    <tr key={a.action} className="border-t border-line">
                      <td className={cx(td, 'num')}>{a.action}</td>
                      <td className={cx(td, num)}>{a.count}</td>
                      <td className={cx(td, num, tone(a.total))}>{a.total === null ? '' : fmtUsd(a.total)}</td>
                    </tr>
                  ))}
                </T>
              ) : (
                <Empty>No staff actions that day.</Empty>
              )}
            </Card>
            <Card title="By staff member">
              {data.byStaff.length ? (
                <T head={['Staff', 'Actions', 'Money actions', 'Money moved']} right={['Actions', 'Money actions', 'Money moved']}>
                  {data.byStaff.map((x) => (
                    <tr key={x.staffId} className="border-t border-line">
                      <td className={td}>
                        {x.name} <span className="text-muted">· {x.role.replace('_', ' ')}</span>
                      </td>
                      <td className={cx(td, num)}>{x.actions}</td>
                      <td className={cx(td, num)}>{x.moneyActions}</td>
                      <td className={cx(td, num)}>{fmtUsd(x.moneyMoved)}</td>
                    </tr>
                  ))}
                </T>
              ) : (
                <Empty>No staff actions that day.</Empty>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
